import { ActivateAppResponseSchema, ListInstalledAppsResponseSchema, ListAppRegistryResponseSchema, LockAppResponseSchema, SetAppAllowedResponseSchema, SetAppConfigRequestSchema, SetAppConfigResponseSchema, SetAppSeatLimitResponseSchema, UpdateAppSeatsResponseSchema } from './app-registry.schema';
/**
 * Activate App Endpoint Contract
 * Enable an unlocked app for the current tenant
 */ export const activateAppContract = {
    method: 'POST',
    path: '/api/v2/apps/:appId/activate',
    description: 'Enable an unlocked app for the current tenant',
    responseSchema: ActivateAppResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - insufficient permissions',
            404: 'App not available for this tenant',
            409: 'App already enabled for this tenant'
        }
    }
};
/**
 * Deactivate App Endpoint Contract
 * Disable an app for the current tenant (preserves data)
 */ export const deactivateAppContract = {
    method: 'POST',
    path: '/api/v2/apps/:appId/deactivate',
    description: 'Disable an app for the current tenant (data preserved)',
    responseSchema: ActivateAppResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - insufficient permissions',
            404: 'App not installed for this tenant'
        }
    }
};
/**
 * List Installed Apps Endpoint Contract
 * Returns enabled apps for the current tenant (used by frontend for discovery)
 */ export const listInstalledAppsContract = {
    method: 'GET',
    path: '/api/v2/apps',
    description: 'List enabled apps for the current tenant',
    responseSchema: ListInstalledAppsResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated'
        }
    }
};
/**
 * List App Registry Endpoint Contract
 * Returns all published apps in the registry (catalog)
 */ export const listAppRegistryContract = {
    method: 'GET',
    path: '/api/v2/app-registry',
    description: 'List all published apps in the registry',
    responseSchema: ListAppRegistryResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated'
        }
    }
};
/**
 * SuperAdmin Unlock App Endpoint Contract
 * Makes an app available to a tenant (adds to org.apps[] with enabled: false)
 */ export const superadminUnlockAppContract = {
    method: 'POST',
    path: '/api/v2/tenants/:tenantId/apps/:appId/unlock',
    description: 'Unlock an app for a tenant (make it available)',
    responseSchema: ActivateAppResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - requires super admin access',
            404: 'App not found in registry',
            409: 'App already unlocked for this tenant'
        }
    }
};
/**
 * SuperAdmin Lock App Endpoint Contract
 * Removes an app from a tenant (removes from org.apps[])
 */ export const superadminLockAppContract = {
    method: 'POST',
    path: '/api/v2/tenants/:tenantId/apps/:appId/lock',
    description: 'Lock an app for a tenant (remove availability)',
    responseSchema: LockAppResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - requires super admin access',
            404: 'App not found or not unlocked for this tenant'
        }
    }
};
/**
 * SuperAdmin Set App Seat Limit Endpoint Contract
 * Caps how many named seats a tenant may assign for an app (`null` = unlimited)
 */ export const superadminSetAppSeatLimitContract = {
    method: 'POST',
    path: '/api/v2/tenants/:tenantId/apps/:appId/seat-limit',
    description: 'Set the named-seat cap for a tenant’s app (null = unlimited)',
    responseSchema: SetAppSeatLimitResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid tenant ID format or seat limit',
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - requires super admin access',
            404: 'App not unlocked for this tenant'
        }
    }
};
/**
 * SuperAdmin Set App Config Endpoint Contract
 * Replaces a tenant's per-app configuration, validated against the app's schema
 * from `getAppConfigSchema`
 */ export const superadminSetAppConfigContract = {
    method: 'PUT',
    path: '/api/v2/tenants/:tenantId/apps/:appId/config',
    description: 'Set the per-tenant configuration of an app',
    requestSchema: SetAppConfigRequestSchema,
    responseSchema: SetAppConfigResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid tenant ID format, app has no configurable surface, or the config does not match the app’s schema',
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - requires super admin access',
            404: 'App not unlocked for this tenant'
        }
    }
};
/**
 * SuperAdmin Set App Allowed Endpoint Contract
 * Grants or withdraws a tenant's permission to use an app, keeping the subdoc —
 * and therefore its seats and config — intact
 */ export const superadminSetAppAllowedContract = {
    method: 'PUT',
    path: '/api/v2/tenants/:tenantId/apps/:appId/allowed',
    description: 'Grant or withdraw a tenant’s permission to use an app',
    responseSchema: SetAppAllowedResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid tenant ID format',
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - requires super admin access',
            404: 'App not unlocked for this tenant'
        }
    }
};
/**
 * Update App Seats Endpoint Contract
 * Replaces the named-seat assignments for an app in the current tenant
 */ export const updateAppSeatsContract = {
    method: 'PUT',
    path: '/api/v2/apps/:appId/seats',
    description: 'Replace the named-seat assignments for an app',
    responseSchema: UpdateAppSeatsResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Seat limit reached or an assigned user does not exist',
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - requires system admin or organization manager access in a customer tenant',
            404: 'App not active for this tenant'
        }
    }
};
/**
 * SuperAdmin List Installed Apps Endpoint Contract
 * Lists installed apps for a specific tenant (superadmin context)
 */ export const superadminListInstalledAppsContract = {
    method: 'GET',
    path: '/api/v2/tenants/:tenantId/apps',
    description: 'List installed apps for a specific tenant (superadmin)',
    responseSchema: ListInstalledAppsResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid tenant ID format',
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - requires super admin access'
        }
    }
};

//# sourceMappingURL=app-registry.contract.js.map